










import {ExpertEstimateState as EstimateState} from '@/model/api';
import {ParameterList} from '@/model/api/parameters';
import ExpertEstimate from '@/model/estimates/ExpertEstimate';
import {HttpClient} from '@/model/http';
import {NotSubmittedParameters, OfflineManager, StagedParameters} from '@/model/offline';
import {StoredParameters} from '@/model/offline/storage/StoredParameters';
import {fromPairs, isEqual} from 'lodash-es';
import {Component, Prop, Provide, Ref, Vue} from 'vue-property-decorator';
import EstimateForm from './common/EstimateForm.vue';
import PhotosSegment from './common/PhotosSegment.vue';
import {LocationSegment, ParametersSegment, SegmentConfiguration} from './common/segments';
import AttachmentsSegment from './common/segments/AttachmentsSegment.vue';
import RecalculationDialog from './realEstateEstimate/RecalculationDialog.vue';

@Component({components: {EstimateForm, RecalculationDialog}})
export default class ExpertEstimateForm extends Vue {

    @Prop({type: Object, required: true})
    public readonly estimate: EstimateState;

    @Provide()
    public readonly notSubmittedParameters: NotSubmittedParameters = new NotSubmittedParameters(
        new StoredParameters(
            this.estimate.id,
            NotSubmittedParameters.STORAGE_KEY,
        ),
    );

    @Provide()
    public readonly model = new ExpertEstimate(this.estimate, new HttpClient());

    @Provide()
    public readonly offlineManager: OfflineManager<EstimateState> = new OfflineManager<EstimateState>(
        new StagedParameters(
            new StoredParameters(
                this.estimate.id,
                StagedParameters.STORAGE_KEY,
            ),
        ),
        this.model,
    );

    @Ref()
    protected readonly recalculationDialog: RecalculationDialog;

    public createSegmentConfiguration(estimate: EstimateState): SegmentConfiguration[] {
        return [
            {
                label: this.$tc('form.segments.location'),
                path: '/adresa',
                component: LocationSegment,
                props: {defaultValue: estimate.location},
            },
            ...estimate.segments.map(segment => ({
                label: segment.label,
                path: '/' + segment.path,
                component: ParametersSegment,
                props: {
                    parameters: Object.fromEntries(
                        segment.parameterNames.map(name => [name, estimate.parameters[name]]),
                    ),
                    stagedParameters: this.offlineManager.stagedParameters,
                    notSubmittedParameters: this.notSubmittedParameters,
                },
            })),
            {
                label: this.$tc('form.segments.photos'),
                path: '/fotografie',
                component: PhotosSegment,
                props: {photos: estimate.photos, estimate},
            },
            {
                label: this.$tc('form.segments.attachments'),
                path: '/prilohy',
                component: AttachmentsSegment,
                props: {
                  availableAttachmentTypes: estimate.availableAttachmentTypes,
                  enabledAttachmentTypes: estimate.enabledAttachmentTypes,
                  attachments: estimate.attachments,
                  estimate,
                },
            },
        ];
    }

    protected onSegmentSave(segmentIndex: number, state: EstimateState, lastKnownState: EstimateState): void {
        if (!this.recalculationAvailable(segmentIndex, state, lastKnownState)) {
            return;
        }

        this.recalculationDialog.show();
    }

    /**
     * Every time any of required parameters is changed, recalculation dialog must be shown
     */
    private recalculationAvailable(segmentIndex: number, state: EstimateState, lastKnownState: EstimateState): boolean {
        if (state.calculationUrl === null) {
            return false; // EstimateState wasn't calculated before
        }

        if (segmentIndex === 0) {
            return !isEqual(state.location, lastKnownState.location);
        }

        return !isEqual(this.getRequiredParameters(state), this.getRequiredParameters(lastKnownState));
    }

    private getRequiredParameters(estimate: EstimateState): ParameterList {
        return fromPairs(
            Object.entries(estimate.parameters).filter(([, definition]) => !definition.optional),
        );
    }
}
